// src/views/contasPagar/ContasPagarList.jsx
import React, { useState, useEffect, useCallback } from "react";
import contasPagarService from "../../services/contasPagarService";
import { STORAGE_OPEN_CP } from "../../services/comprasService";
import condominiosService from "../../services/condominiosService";
import { useAuth } from "../../contexts/AuthContext";
import ContaPagarModal from "./ContaPagarModal";
import PagamentoModal from "./PagamentoModal";
import DeleteModal from "../../components/ui/DeleteModal";
import Banner from "../../components/ui/Banner";

const fmtMoeda = v => parseFloat(v||0).toLocaleString("pt-BR",{style:"currency",currency:"BRL"});
const fmtDt    = dt => {if(!dt)return"-";try{return new Date((String(dt||"").split("T")[0])+"T12:00:00").toLocaleDateString("pt-BR");}catch{return dt;}};

const STATUS_STYLES = {
  Aberto:      {background:"var(--bg-highlight)",color:"var(--primary)"},
  PagoParcial: {background:"#FFF7ED",color:"#C2410C"},
  Pago:        {background:"var(--bg-success-soft)",color:"#166534"},
  Cancelado:   {background:"var(--bg-danger-soft)",color:"#991B1B"},
};
const APROV_STYLES = {
  Dispensado:  {background:"var(--gray-100)",color:"var(--gray-500)"},
  Pendente:    {background:"var(--bg-warning-soft)",color:"#854D0E"},
  Aprovado:    {background:"var(--bg-success-soft)",color:"#166534"},
  Rejeitado:   {background:"var(--bg-danger-soft)",color:"#991B1B"},
};

export default function ContasPagarList() {
  const { condominioAtivo, isAdmin, isGerente } = useAuth();
  const { podeEditar } = useAuth();
  const canEdit = podeEditar("contasPagar");
  const [data,setData]=useState([]);
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState(null);
  const [pagination,setPagination]=useState({total:0,totalPages:1});
  const [condominios,setCondominios]=useState([]);

  useEffect(() => {
    condominiosService.listar({limit:100}).then(r=>setCondominios(r.data||[])).catch(()=>{});
  }, []);

  // Sync with navbar condominioAtivo — updates whenever user changes it
  useEffect(() => {
    if (condominioAtivo?.id) setIdCondo(String(condominioAtivo.id));
    else setIdCondo("");
  }, [condominioAtivo]);
  const [idCondo,setIdCondo]=useState("");
  const [filtros,setFiltros]=useState({idFornecedor:"",status:"",dtVencIni:"",dtVencFim:"",page:1});
  const [showModal,setShowModal]=useState(false);
  const [editData,setEditData]=useState(null);
  const [showPagamento,setShowPagamento]=useState(false);
  const [editVenc,    setEditVenc]    = useState(null);
  const [editVencVal, setEditVencVal] = useState("");
  const [parcelaPagar,setParcelaPagar]=useState(null);
  const [showDelete,setShowDelete]=useState(false);
  const [deleteTarget,setDeleteTarget]=useState(null);
  const [saving,setSaving]=useState(false);
  const [savingPag,setSavingPag]=useState(false);
  const [deleting,setDeleting]=useState(false);
  const [abaDetalhe,setAbaDetalhe]=useState(null);
  const [detalhe,setDetalhe]=useState(null);
  const [aba,setAba]=useState("lista"); // lista | relatorio
  const [relatorio,setRelatorio]=useState(null);
  const [filtrosRel,setFiltrosRel]=useState({dtVencIni:"",dtVencFim:"",idFornecedor:"",status:"",agrupar:"fornecedor"});
  const [loadingRel,setLoadingRel]=useState(false);

  // Banner global de feedback (sucesso/erro/info)
  const [banner, setBanner] = useState(null);
  const showBanner = (tipo, msg) => { setBanner({ tipo, msg }); setTimeout(()=>setBanner(null), 6000); };

  // Modal de rejeição (em vez de window.prompt)
  const [showRejeitar, setShowRejeitar] = useState(false);
  const [rejeitarTarget, setRejeitarTarget] = useState(null);
  const [rejeitarMotivo, setRejeitarMotivo] = useState("");
  const [rejeitarSaving, setRejeitarSaving] = useState(false);

  const carregar=useCallback(async()=>{
    if(!idCondo)return;setLoading(true);setError(null);
    try{const r=await contasPagarService.listar({idCondominio:idCondo,...filtros,limit:20});setData(r.data);setPagination(r.pagination);}
    catch(e){setError(e.message);}finally{setLoading(false);}
  },[idCondo,filtros]);

  useEffect(()=>{carregar();},[carregar]);

  useEffect(() => {
    if (!idCondo) return;
    const raw = sessionStorage.getItem(STORAGE_OPEN_CP);
    if (!raw) return;
    sessionStorage.removeItem(STORAGE_OPEN_CP);
    contasPagarService.buscarPorId(raw)
      .then((r) => { setEditData(r.data); setShowModal(true); })
      .catch(() => showBanner("danger", "Conta a pagar não encontrada."));
  }, [idCondo]);

  const condoNome=condominios.find(c=>String(c.id)===String(idCondo))?.nmFantasia||"";
  const setF=(f,v)=>setFiltros(p=>({...p,[f]:v,page:1}));
  const totalPages=pagination.totalPages||1;

  // Totais da listagem
  const totais=data.reduce((a,c)=>{
    a.total+=parseFloat(c.vlrLiquido||0);
    a.emAberto+=parseFloat(c.vlrEmAberto||0);
    if(c.status==="Pago")a.pago+=parseFloat(c.vlrLiquido||0);
    if(c.proxVencimento&&new Date(c.proxVencimento)<new Date()&&c.status!=="Pago")a.vencido+=parseFloat(c.vlrEmAberto||0);
    return a;
  },{total:0,emAberto:0,pago:0,vencido:0});

  const handleSave=async(form)=>{
    setSaving(true);
    try{
      const payload={...form,idCondominio:idCondo};
      if(form.id)await contasPagarService.atualizar(form.id,payload);
      else await contasPagarService.criar(payload);
      setShowModal(false);
      showBanner("success", form.id ? "Conta atualizada com sucesso." : "Conta a pagar lançada com sucesso.");
      await carregar();
    }catch(e){showBanner("error", e.message || "Erro ao salvar.");}finally{setSaving(false);}
  };

  const handlePagar=async(idParcela,formPag)=>{
    setSavingPag(true);
    try{
      const r=await contasPagarService.pagarParcela(idParcela,formPag);
      setShowPagamento(false);setParcelaPagar(null);
      showBanner("success", r.message || "Pagamento registrado com sucesso.");
      if(abaDetalhe){const rd=await contasPagarService.buscarPorId(abaDetalhe);setDetalhe(rd.data);}
      await carregar();
    }catch(e){showBanner("error", e.message || "Não foi possível registrar o pagamento.");}finally{setSavingPag(false);}
  };

  const handleCancelar=async()=>{
    setDeleting(true);
    try{
      await contasPagarService.cancelar(deleteTarget.id);
      setShowDelete(false);setDeleteTarget(null);
      showBanner("success", "Conta cancelada com sucesso.");
      await carregar();
    }
    catch(e){showBanner("error", e.message || "Erro ao cancelar.");}finally{setDeleting(false);}
  };

  const handleAprovar=async(id)=>{
    try{
      await contasPagarService.aprovar(id);
      showBanner("success", "Conta aprovada.");
      await carregar();
    }catch(e){showBanner("error", e.message || "Erro ao aprovar.");}
  };

  const abrirRejeitar = (conta) => {
    setRejeitarTarget(conta);
    setRejeitarMotivo("");
    setShowRejeitar(true);
  };

  const confirmarRejeitar = async () => {
    if (!rejeitarMotivo.trim()) {
      showBanner("warning", "Informe um motivo para a rejeição.");
      return;
    }
    setRejeitarSaving(true);
    try {
      await contasPagarService.rejeitar(rejeitarTarget.id, rejeitarMotivo);
      setShowRejeitar(false);
      setRejeitarTarget(null);
      showBanner("success", "Conta rejeitada.");
      await carregar();
    } catch (e) { showBanner("error", e.message || "Erro ao rejeitar."); }
    finally { setRejeitarSaving(false); }
  };

  const handleSalvarVenc = async () => {
    if (!editVenc || !editVencVal) return;
    try {
      await contasPagarService.editarVencimentoParcela(editVenc.id, editVencVal);
      setEditVenc(null);
      showBanner("success", "Vencimento atualizado.");
      if (detalhe?.id) abrirDetalhe(detalhe.id);
    } catch(e) { showBanner("error", e?.message||"Erro ao alterar vencimento."); }
  };

  const abrirDetalhe=async(id)=>{
    try{const r=await contasPagarService.buscarPorId(id);setDetalhe(r.data);setAbaDetalhe(id);}catch(e){showBanner("error", e.message || "Erro ao abrir detalhe.");}
  };

  const gerarRelatorio=async()=>{
    setLoadingRel(true);
    try{const r=await contasPagarService.relatorio({idCondominio:idCondo,...filtrosRel});setRelatorio(r);}
    catch(e){showBanner("error", e.message || "Erro ao gerar relatório.");}finally{setLoadingRel(false);}
  };

  const setFR=(f,v)=>setFiltrosRel(p=>({...p,[f]:v}));

  return(
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title"><i className="bi bi-receipt me-2" style={{color:"var(--primary)",fontSize:22}}/>Contas a Pagar</h1>
          <p className="page-subtitle">Lançamentos, parcelas e pagamentos</p>
        </div>
        <div className="d-flex gap-2 flex-wrap align-items-center">

          <button className="btn btn-primary" onClick={()=>{setEditData(null);setShowModal(true);}} disabled={!idCondo || !canEdit}><i className="bi bi-plus-lg"/> Nova Conta</button>
        </div>
      </div>

      {banner && (
        <Banner tipo={banner.tipo} onClose={() => setBanner(null)}>
          {banner.msg}
        </Banner>
      )}

      {/* Stats */}
      <div className="row g-3 mb-4">
        {[
          {icon:"bi-receipt",cls:"blue",val:fmtMoeda(totais.total),label:"Total Lançado"},
          {icon:"bi-hourglass-split",cls:"orange",val:fmtMoeda(totais.emAberto),label:"Em Aberto"},
          {icon:"bi-exclamation-circle",cls:"gray",val:fmtMoeda(totais.vencido),label:"Vencido"},
          {icon:"bi-check-circle",cls:"green",val:fmtMoeda(totais.pago),label:"Pago"},
        ].map(s=>(
          <div className="col-6 col-md-3" key={s.label}>
            <div className="stat-card">
              <div className={`stat-icon ${s.cls}`}><i className={`bi ${s.icon}`}/></div>
              <div><div className="stat-value" style={{fontSize:16}}>{s.val}</div><div className="stat-label">{s.label}</div></div>
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        {/* Sub-abas */}
        <div style={{borderBottom:"1px solid var(--gray-200)",padding:"0 16px",display:"flex",gap:4}}>
          {[{id:"lista",label:"Lançamentos",icon:"bi-list-ul"},{id:"relatorio",label:"Relatório",icon:"bi-bar-chart"}].map(t=>(
            <button key={t.id} onClick={()=>setAba(t.id)}
              style={{fontSize:13,padding:"12px 14px",background:"transparent",border:"none",cursor:"pointer",borderBottom:aba===t.id?"2px solid var(--primary)":"2px solid transparent",fontWeight:aba===t.id?600:400,color:aba===t.id?"var(--primary)":"var(--gray-500)",display:"flex",alignItems:"center",gap:6}}>
              <i className={`bi ${t.icon}`}/>{t.label}
            </button>
          ))}
        </div>

        {/* LISTA */}
        {aba==="lista"&&(
          <>
            <div style={{padding:"12px 16px",borderBottom:"1px solid var(--gray-100)",display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
              <input type="date" className="form-control form-control-sm" style={{width:"auto"}} value={filtros.dtVencIni} onChange={e=>setF("dtVencIni",e.target.value)} title="Venc. de"/>
              <input type="date" className="form-control form-control-sm" style={{width:"auto"}} value={filtros.dtVencFim} onChange={e=>setF("dtVencFim",e.target.value)} title="Venc. até"/>
              <select className="form-select form-select-sm" style={{width:"auto"}} value={filtros.status} onChange={e=>setF("status",e.target.value)}>
                <option value="">Todos</option>
                {["Aberto","PagoParcial","Pago","Cancelado"].map(s=><option key={s}>{s}</option>)}
              </select>
              <button className="btn btn-outline-secondary btn-sm" onClick={carregar}><i className="bi bi-arrow-clockwise"/></button>
            </div>

            {error&&<div className="alert alert-danger m-3 mb-0" style={{fontSize:13}}>{error}</div>}

            <div className="table-responsive">
              <table className="table table-custom">
                <thead><tr><th>Documento</th><th>Fornecedor</th><th>Conta Contábil</th><th>Vencimento</th><th style={{textAlign:"right"}}>Líquido</th><th style={{textAlign:"right"}}>Em Aberto</th><th>Aprovação</th><th>Status</th><th style={{width:110}}>Ações</th></tr></thead>
                <tbody>
                  {loading?<tr><td colSpan={9} className="text-center py-5"><span className="spinner-border spinner-border-sm text-primary me-2"/></td></tr>
                  :!idCondo?<tr><td colSpan={9} className="text-center py-4" style={{color:"var(--gray-400)"}}>Selecione um condomínio</td></tr>
                  :data.length===0?<tr><td colSpan={9} className="text-center py-4" style={{color:"var(--gray-400)"}}>Nenhuma conta encontrada</td></tr>
                  :data.map(c=>(
                    <React.Fragment key={c.id}>
                    <tr>
                      <td>
                        <div style={{fontWeight:600,fontSize:13}}>{c.tpDocumento} {c.nrDocumento||""}</div>
                        <div style={{fontSize:11.5,color:"var(--gray-400)",maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.descricao}</div>
                      </td>
                      <td style={{fontSize:13}}>{c.nmFornecedor||<span style={{color:"var(--gray-300)"}}>—</span>}</td>
                      <td style={{fontSize:12,color:"var(--gray-500)",maxWidth:120,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.nmContaContabil||"—"}</td>
                      <td style={{fontSize:12.5,whiteSpace:"nowrap"}}>{c.proxVencimento?fmtDt(c.proxVencimento):"—"}</td>
                      <td style={{textAlign:"right",fontWeight:700,fontSize:13}}>{fmtMoeda(c.vlrLiquido)}</td>
                      <td style={{textAlign:"right",fontWeight:700,fontSize:13,color:parseFloat(c.vlrEmAberto)>0?"#991B1B":"var(--gray-400)"}}>{fmtMoeda(c.vlrEmAberto)}</td>
                      <td>
                        <span style={{...APROV_STYLES[c.statusAprovacao],padding:"2px 8px",borderRadius:12,fontSize:11,fontWeight:600}}>{c.statusAprovacao}</span>
                        {c.statusAprovacao==="Pendente"&&(isAdmin||c.idCondominio)&&(
                          <>
                            <button className="btn btn-link btn-sm p-0 ms-1" style={{fontSize:11,color:"#166534"}} onClick={()=>handleAprovar(c.id)} title="Aprovar"><i className="bi bi-check2"/></button>
                            <button className="btn btn-link btn-sm p-0 ms-1" style={{fontSize:11,color:"#991B1B"}} onClick={()=>abrirRejeitar(c)} title="Rejeitar"><i className="bi bi-x-circle"/></button>
                          </>
                        )}
                      </td>
                      <td><span style={{...STATUS_STYLES[c.status],padding:"2px 8px",borderRadius:12,fontSize:11,fontWeight:600}}>{c.status}</span></td>
                      <td>
                        <div className="table-actions">
                          <button className="btn btn-outline-secondary btn-sm btn-icon" title="Ver parcelas" onClick={()=>abrirDetalhe(c.id)}><i className="bi bi-eye"/></button>
                          {c.status!=="Cancelado"&&c.status!=="Pago"&&<button className="btn btn-outline-secondary btn-sm btn-icon" title="Editar" onClick={()=>{setEditData(c);setShowModal(true);}} disabled={!canEdit}><i className="bi bi-pencil"/></button>}
                          <button className="btn btn-outline-secondary btn-sm btn-icon" title="Duplicar para novo lançamento"
                            onClick={async()=>{
                              try {
                                const r = await contasPagarService.buscarPorId(c.id);
                                const orig = r.data || {};
                                // Cria um clone sem ids, datas e status (vira lançamento novo)
                                const clone = {
                                  ...orig,
                                  id: undefined,
                                  status: undefined,
                                  statusAprovacao: undefined,
                                  dtEmissao: "",
                                  dtCompetencia: "",
                                  aprovadoPor: undefined,
                                  dtAprovacao: undefined,
                                  motivoRejeicao: undefined,
                                  anexoBase64: "",
                                  parcelas: [],
                                };
                                setEditData(clone);
                                setShowModal(true);
                              } catch(e) { showBanner("error", e.message); }
                            }} disabled={!canEdit}>
                            <i className="bi bi-files"/>
                          </button>
                          {c.status!=="Cancelado"&&c.status!=="Pago"&&<button className="btn btn-outline-danger btn-sm btn-icon" title="Cancelar" onClick={()=>{setDeleteTarget(c);setShowDelete(true);}} disabled={!canEdit}><i className="bi bi-x-circle"/></button>}
                        </div>
                      </td>
                    </tr>
                    {/* Parcelas inline */}
                    {abaDetalhe===c.id && detalhe && (
                      <tr>
                        <td colSpan={9} style={{padding:0,background:"var(--bg-surface-alt)",borderBottom:"2px solid var(--primary)"}}>
                          <div style={{padding:"12px 16px"}}>
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                              <span style={{fontWeight:700,fontSize:13,color:"var(--gray-700)"}}>
                                <i className="bi bi-calendar3 me-2"/>Parcelas — {detalhe.descricao}
                              </span>
                              <button className="btn btn-outline-secondary btn-sm" onClick={()=>{setAbaDetalhe(null);setDetalhe(null);}}>
                                <i className="bi bi-x"/>
                              </button>
                            </div>
                            <table className="table table-custom mb-0" style={{background:"var(--bg-surface)",fontSize:12}}>
                              <thead><tr><th>#</th><th>Vencimento</th><th style={{textAlign:"right"}}>Valor</th><th style={{textAlign:"right"}}>Pago</th><th>Dt. Pgto</th><th>Status</th><th>Ação</th></tr></thead>
                              <tbody>
                                {(detalhe.parcelas||[]).map(p=>(
                                  <tr key={p.id}>
                                    <td style={{fontWeight:600}}>#{p.nrParcela||1}</td>
                                    <td>{fmtDt(p.dtVencimento)}</td>
                                    <td style={{textAlign:"right",fontWeight:600}}>{fmtMoeda(p.vlrParcela)}</td>
                                    <td style={{textAlign:"right",color:"#166534",fontWeight:600}}>{p.vlrPago?fmtMoeda(p.vlrPago):"—"}</td>
                                    <td>{p.dtPagamento?fmtDt(p.dtPagamento):"—"}</td>
                                    <td><span style={{fontSize:10,padding:"2px 8px",borderRadius:10,fontWeight:700,
                                      background:p.status==="Pago"?"#DCFCE7":p.status==="Vencido"?"#FEE2E2":"#FEF3C7",
                                      color:p.status==="Pago"?"#166534":p.status==="Vencido"?"#991B1B":"#92400E"}}>{p.status}</span></td>
                                    <td>
                                      {p.status!=="Pago"&&p.status!=="Cancelado"&&(
                                        <button className="btn btn-success btn-sm" style={{fontSize:11}} onClick={()=>{setParcelaPagar(p);setShowPagamento(true);}}>
                                          <i className="bi bi-check2 me-1"/>Pagar
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Detalhe parcelas - REMOVIDO (agora inline) */}
            {false&&abaDetalhe&&detalhe&&(
              <div style={{margin:"16px",background:"var(--bg-surface-alt)",border:"1px solid var(--gray-200)",borderRadius:8,padding:16}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                  <span style={{fontWeight:700,fontSize:14,color:"var(--gray-700)"}}><i className="bi bi-calendar3 me-2"/>Parcelas — {detalhe.descricao}</span>
                  <button className="btn btn-outline-secondary btn-sm" onClick={()=>{setAbaDetalhe(null);setDetalhe(null);}}><i className="bi bi-x"/></button>
                </div>
                <table className="table table-custom" style={{background:"var(--bg-surface)"}}>
                  <thead><tr><th>#</th><th>Vencimento</th><th>Valor</th><th>Pago</th><th>Dt. Pgto</th><th>Forma</th><th>Status</th><th>Ação</th></tr></thead>
                  <tbody>
                    {(detalhe.parcelas||[]).map(p=>(
                      <tr key={p.id}>
                        <td style={{fontWeight:700,color:"var(--primary)"}}>{p.nrParcela}</td>
                        <td style={{fontSize:12.5}}>{fmtDt(p.dtVencimento)}</td>
                        <td style={{fontWeight:600}}>{fmtMoeda(p.vlrParcela)}</td>
                        <td style={{color:"#166534",fontWeight:600}}>{p.vlrPago?fmtMoeda(p.vlrPago):"—"}</td>
                        <td style={{fontSize:12.5}}>{fmtDt(p.dtPagamento)}</td>
                        <td style={{fontSize:12}}>{p.tpPagamento||"—"}</td>
                        <td><span style={{...STATUS_STYLES[p.status]||{},padding:"2px 8px",borderRadius:12,fontSize:11,fontWeight:600}}>{p.status}</span></td>
                        <td>
                          {p.status==="Aberto"&&(
                            <button className="btn btn-outline-secondary btn-sm btn-icon" title="Alterar vencimento"
                              onClick={()=>{setEditVenc(p);setEditVencVal(p.dtVencimento?String(p.dtVencimento).split("T")[0]:"");}}>
                              <i className="bi bi-calendar-event"/>
                            </button>
                          )}
                          {p.status==="Aberto"&&(detalhe.statusAprovacao==="Aprovado"||detalhe.statusAprovacao==="Dispensado")&&(
                            <button className="btn btn-success btn-sm" style={{fontSize:11}} onClick={()=>{setParcelaPagar({...p,nmFornecedor:detalhe.nmFornecedor,descricao:detalhe.descricao,pixFornecedor:detalhe.pixFornecedor});setShowPagamento(true);}}>
                              <i className="bi bi-cash-coin"/> Pagar
                            </button>
                          )}
                          {p.status==="Aberto"&&detalhe.statusAprovacao==="Pendente"&&(
                            <span style={{fontSize:11,color:"#854D0E"}}>Aguarda aprovação</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Paginação */}
            <div className="pagination-wrapper">
              <span className="pagination-info">{pagination.total} registro(s)</span>
              <nav><ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${filtros.page===1?"disabled":""}`}><button className="page-link" onClick={()=>setF("page",filtros.page-1)}><i className="bi bi-chevron-left"/></button></li>
                {Array.from({length:totalPages},(_,i)=>(<li key={i+1} className={`page-item ${filtros.page===i+1?"active":""}`}><button className="page-link" onClick={()=>setF("page",i+1)}>{i+1}</button></li>))}
                <li className={`page-item ${filtros.page===totalPages?"disabled":""}`}><button className="page-link" onClick={()=>setF("page",filtros.page+1)}><i className="bi bi-chevron-right"/></button></li>
              </ul></nav>
            </div>
          </>
        )}

        {/* RELATÓRIO */}
        {aba==="relatorio"&&(
          <div style={{padding:16}}>
            {/* Filtros */}
            <div style={{background:"var(--gray-50)",border:"1px solid var(--gray-200)",borderRadius:8,padding:"14px 16px",marginBottom:16}}>
              <div className="row g-2 align-items-end">
                <div className="col-md-2"><label className="form-label" style={{fontSize:12}}>Venc. de</label><input type="date" className="form-control form-control-sm" value={filtrosRel.dtVencIni} onChange={e=>setFR("dtVencIni",e.target.value)}/></div>
                <div className="col-md-2"><label className="form-label" style={{fontSize:12}}>Venc. até</label><input type="date" className="form-control form-control-sm" value={filtrosRel.dtVencFim} onChange={e=>setFR("dtVencFim",e.target.value)}/></div>
                <div className="col-md-2"><label className="form-label" style={{fontSize:12}}>Situação</label>
                  <select className="form-select form-select-sm" value={filtrosRel.status} onChange={e=>setFR("status",e.target.value)}>
                    <option value="">Todos</option>
                    <option value="Aberto">Em Aberto</option><option value="Pago">Pago</option><option value="Vencido">Vencido</option>
                  </select>
                </div>
                <div className="col-md-2"><label className="form-label" style={{fontSize:12}}>Agrupar por</label>
                  <select className="form-select form-select-sm" value={filtrosRel.agrupar} onChange={e=>setFR("agrupar",e.target.value)}>
                    <option value="fornecedor">Fornecedor</option>
                    <option value="periodo">Período</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <button className="btn btn-primary w-100 btn-sm" onClick={gerarRelatorio} disabled={loadingRel||!idCondo}>
                    {loadingRel?<span className="spinner-border spinner-border-sm"/>:<><i className="bi bi-bar-chart"/> Gerar</>}
                  </button>
                </div>
              </div>
            </div>

            {relatorio&&(
              <>
                {/* Totalizadores */}
                <div style={{display:"flex",gap:12,marginBottom:20,flexWrap:"wrap"}}>
                  {[
                    {label:"Total Lançado",val:relatorio.totais.total,color:"var(--gray-700)"},
                    {label:"Em Aberto",val:relatorio.totais.aberto,color:"var(--primary)"},
                    {label:"Vencido",val:relatorio.totais.vencido,color:"#991B1B"},
                    {label:"Pago",val:relatorio.totais.pago,color:"#166534"},
                    {label:"Saldo Pendente",val:relatorio.totais.saldo,color:"#C2410C"},
                  ].map(t=>(
                    <div key={t.label} style={{flex:1,minWidth:140,background:"var(--bg-surface)",border:"1px solid var(--gray-200)",borderRadius:8,padding:"14px 16px",textAlign:"center"}}>
                      <div style={{fontSize:20,fontWeight:700,color:t.color}}>{fmtMoeda(t.val)}</div>
                      <div style={{fontSize:12,color:"var(--gray-500)",marginTop:2}}>{t.label}</div>
                    </div>
                  ))}
                </div>

                {/* Grupos */}
                {relatorio.grupos.map((grupo,gi)=>(
                  <div key={gi} style={{marginBottom:20}}>
                    <div style={{fontWeight:700,fontSize:14,color:"var(--primary)",padding:"8px 12px",background:"var(--bg-highlight)",borderRadius:"8px 8px 0 0",display:"flex",justifyContent:"space-between"}}>
                      <span>{grupo.nome||grupo.periodo}</span>
                      <span>{fmtMoeda(grupo.subtotal)}</span>
                    </div>
                    <table className="table table-custom" style={{marginBottom:0}}>
                      <thead><tr><th>Vencimento</th><th>Descrição</th><th>Nº Doc.</th><th>Parcela</th><th>Forma Pgto</th><th style={{textAlign:"right"}}>Valor</th><th>Status</th></tr></thead>
                      <tbody>
                        {grupo.parcelas.map(p=>(
                          <tr key={p.id}>
                            <td style={{fontSize:12.5,whiteSpace:"nowrap"}}>{fmtDt(p.dtVencimento)}</td>
                            <td style={{fontSize:13}}>{p.descricao}</td>
                            <td style={{fontSize:12,color:"var(--gray-500)"}}>{p.nrDocumento||"—"}</td>
                            <td style={{fontSize:12.5}}>Parc. {p.nrParcela}</td>
                            <td style={{fontSize:12}}>{p.tpPagamento||"—"}</td>
                            <td style={{textAlign:"right",fontWeight:700,fontSize:13}}>{fmtMoeda(p.vlrParcela)}</td>
                            <td><span style={{...STATUS_STYLES[p.status]||{},padding:"2px 8px",borderRadius:12,fontSize:11,fontWeight:600}}>{p.status}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      <ContaPagarModal show={showModal} onClose={()=>setShowModal(false)} onSave={handleSave} editData={editData} saving={saving} idCondominio={idCondo} condominioNome={condoNome}/>
      <PagamentoModal show={showPagamento} onClose={()=>setShowPagamento(false)} onPagar={handlePagar} parcela={parcelaPagar} saving={savingPag} idCondominio={idCondo}/>
      <DeleteModal show={showDelete} onClose={()=>setShowDelete(false)} onConfirm={handleCancelar} itemName={deleteTarget?.descricao} loading={deleting}/>

      {/* Modal de rejeição (substitui window.prompt) */}
      {showRejeitar && (
        <div className="modal-overlay" onClick={()=>!rejeitarSaving && setShowRejeitar(false)}>
          <div className="modal-box" style={{maxWidth:460}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h6 className="modal-title"><i className="bi bi-x-octagon me-2"/>Rejeitar Conta a Pagar</h6>
              <button className="btn-close" onClick={()=>setShowRejeitar(false)} disabled={rejeitarSaving}/>
            </div>
            <div className="modal-body">
              <p style={{fontSize:13,color:"var(--gray-500)",marginBottom:10}}>
                Você está rejeitando: <strong>{rejeitarTarget?.descricao}</strong>
                {rejeitarTarget?.nmFornecedor && ` — ${rejeitarTarget.nmFornecedor}`}
              </p>
              <label className="form-label fw-semibold" style={{fontSize:13}}>
                Motivo da rejeição <span style={{color:"#991B1B"}}>*</span>
              </label>
              <textarea className="form-control" rows={3} value={rejeitarMotivo}
                onChange={e=>setRejeitarMotivo(e.target.value)}
                placeholder="Ex.: documento divergente, valor incorreto, fornecedor não autorizado..."
                autoFocus/>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary btn-sm" onClick={()=>setShowRejeitar(false)} disabled={rejeitarSaving}>Cancelar</button>
              <button className="btn btn-danger btn-sm" onClick={confirmarRejeitar} disabled={rejeitarSaving||!rejeitarMotivo.trim()}>
                {rejeitarSaving ? <span className="spinner-border spinner-border-sm me-1"/> : <i className="bi bi-x-octagon me-1"/>}
                Confirmar Rejeição
              </button>
            </div>
          </div>
        </div>
      )}
      {editVenc && (
        <div className="modal-overlay" onClick={()=>setEditVenc(null)}>
          <div className="modal-box" style={{maxWidth:360}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h6 className="modal-title"><i className="bi bi-calendar-event me-2"/>Alterar Vencimento</h6>
              <button className="btn-close" onClick={()=>setEditVenc(null)}/>
            </div>
            <div className="modal-body">
              <p style={{fontSize:13,color:"var(--gray-500)",marginBottom:12}}>
                Parcela <strong>{editVenc.nrParcela}</strong> — {fmtMoeda(editVenc.vlrParcela)}
              </p>
              <label className="form-label fw-semibold" style={{fontSize:13}}>Nova Data de Vencimento</label>
              <input type="date" className="form-control" value={editVencVal}
                onChange={e=>setEditVencVal(e.target.value)} autoFocus/>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary btn-sm" onClick={()=>setEditVenc(null)}>Cancelar</button>
              <button className="btn btn-primary btn-sm" onClick={handleSalvarVenc}>
                <i className="bi bi-check-lg me-1"/>Salvar Vencimento
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
